<template>
  <div
    class="gallery"
    :class="[`gallery--${shape}`, { 'gallery--single': images.length < 2 }]"
  >
    <div class="gallery__main">
      <div class="gallery__stage">
        <div
          ref="track"
          class="gallery__track"
          role="region"
          aria-roledescription="carousel"
          :aria-label="`${title} images`"
          tabindex="0"
          :style="{ aspectRatio: frameRatio }"
          @scroll.passive="onScroll"
          @keydown.left.prevent="go(active - 1)"
          @keydown.right.prevent="go(active + 1)"
        >
          <button
            v-for="(img, i) in images"
            :key="img.url"
            type="button"
            class="gallery__slide"
            :aria-label="`Enlarge image ${i + 1} of ${images.length}`"
            @click="openLightbox"
          >
            <img
              :src="sized(img.url, 1200)"
              :srcset="`${sized(img.url, 800)} 800w, ${sized(img.url, 1200)} 1200w, ${sized(img.url, 1800)} 1800w`"
              sizes="(max-width: 960px) 100vw, 55vw"
              :alt="img.alt"
              :width="img.width"
              :height="img.height"
              :loading="i === 0 ? 'eager' : 'lazy'"
              draggable="false"
            />
          </button>
        </div>

        <template v-if="images.length > 1">
          <button
            type="button"
            class="gallery__arrow gallery__arrow--prev"
            aria-label="Previous image"
            :disabled="active === 0"
            @click="go(active - 1)"
          >‹</button>
          <button
            type="button"
            class="gallery__arrow gallery__arrow--next"
            aria-label="Next image"
            :disabled="active === images.length - 1"
            @click="go(active + 1)"
          >›</button>
        </template>
      </div>

      <button type="button" class="gallery__enlarge" @click="openLightbox">
        <span class="gallery__enlarge-label">Click to enlarge</span>
        <span class="gallery__enlarge-label gallery__enlarge-label--touch">Tap to enlarge</span>
      </button>
    </div>

    <div v-if="images.length > 1" class="gallery__thumbs">
      <button
        v-for="(img, i) in images"
        :key="img.url"
        type="button"
        class="gallery__thumb"
        :class="{ 'is-active': i === active }"
        :aria-label="`Show image ${i + 1}`"
        :aria-current="i === active"
        @click="go(i)"
      >
        <img :src="sized(img.url, 240)" :alt="''" loading="lazy" draggable="false" />
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="`${title}, detail view`"
        @click.self="closeLightbox"
      >
        <button
          ref="closeBtn"
          type="button"
          class="lightbox__close"
          aria-label="Close"
          @click="closeLightbox"
        >×</button>
        <div
          ref="scroller"
          class="lightbox__scroller"
          :class="{ 'is-zoomed': zoomed }"
          @click.self="closeLightbox"
        >
          <img
            :src="zoomed ? detail.url : sized(detail.url, 1800)"
            :alt="detail.alt"
            class="lightbox__img"
            draggable="false"
            @click="toggleZoom"
          />
        </div>
        <p class="lightbox__hint">{{ zoomed ? 'Tap to fit' : 'Tap to zoom' }}</p>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'ProductGallery',

  props: {
    images: { type: Array, required: true },
    title: { type: String, default: '' },
  },

  data() {
    return {
      active: 0,
      lightboxOpen: false,
      zoomed: false,
    }
  },

  computed: {
    // The lead shot sets the gallery's shape for every slide, so the frame
    // never resizes. It takes the lead's exact ratio (within reason) so the
    // photo fills it and its top edge lines up with the thumbnails; other
    // shots sit inside uncropped, pinned to the top.
    shape() {
      return this.images[0]?.orientation || 'portrait'
    },
    frameRatio() {
      const ratio = this.images[0]?.ratio
      if (!ratio) return '4 / 5'
      return String(Math.min(Math.max(ratio, 0.6), 3))
    },
    // The last image is the plain, full-on detail shot, so that's what
    // enlarging shows regardless of which slide was clicked.
    detail() {
      return this.images[this.images.length - 1]
    },
  },

  beforeUnmount() {
    this.unlockScroll()
  },

  methods: {
    // Shopify's CDN resizes on the fly via the width param.
    sized(url, width) {
      return `${url}${url.includes('?') ? '&' : '?'}width=${width}`
    },
    go(i) {
      const track = this.$refs.track
      if (!track || i < 0 || i >= this.images.length) return
      this.active = i
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' })
    },
    onScroll() {
      const track = this.$refs.track
      this.active = Math.round(track.scrollLeft / track.clientWidth)
    },
    openLightbox() {
      this.lightboxOpen = true
      this.zoomed = false
      document.documentElement.style.overflow = 'hidden'
      window.addEventListener('keydown', this.onKeydown)
      this.$nextTick(() => this.$refs.closeBtn?.focus())
    },
    closeLightbox() {
      this.lightboxOpen = false
      this.unlockScroll()
    },
    unlockScroll() {
      document.documentElement.style.overflow = ''
      window.removeEventListener('keydown', this.onKeydown)
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.closeLightbox()
    },
    toggleZoom(e) {
      const img = e.currentTarget
      // Keep the tapped point roughly under the finger/cursor after zooming in.
      const rect = img.getBoundingClientRect()
      const fx = (e.clientX - rect.left) / rect.width
      const fy = (e.clientY - rect.top) / rect.height
      this.zoomed = !this.zoomed
      if (!this.zoomed) return
      this.$nextTick(() => {
        const el = this.$refs.scroller
        const center = () => {
          el.scrollLeft = fx * el.scrollWidth - el.clientWidth / 2
          el.scrollTop = fy * el.scrollHeight - el.clientHeight / 2
        }
        if (img.complete) center()
        img.addEventListener('load', center, { once: true })
      })
    },
  },
}
</script>

<style scoped>
.gallery {
  display: grid;
  grid-template-columns: 8rem minmax(0, 1fr);
  gap: 1.6rem;
  align-items: start;
}
.gallery--single { grid-template-columns: minmax(0, 1fr); }

/* Main image comes first in the DOM for keyboard order; thumbs sit to its left. */
.gallery__main {
  grid-column: -2 / -1;
  grid-row: 1;
  min-width: 0;
}

.gallery__stage {
  position: relative;
}

.gallery__track {
  width: 100%;
  display: flex;
  /* Keep the whole gallery visible beside the sticky details on desktop. */
  max-height: calc(100vh - 22rem);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  overscroll-behavior-x: contain;
}
.gallery__track::-webkit-scrollbar { display: none; }
.gallery__track:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

.gallery__slide {
  flex: 0 0 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  padding: 0;
  border: 0;
  background: none;
  cursor: zoom-in;
}

.gallery__slide img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  /* Top-left keeps every slide flush with the top of the thumb column and
     tucked against it when the frame is wider than the photo. */
  object-position: left top;
  user-select: none;
}

.gallery__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 4.4rem;
  height: 4.4rem;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: rgba(248, 243, 234, 0.85);
  color: var(--ink);
  font-size: 2.6rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.gallery__arrow--prev { left: 1.2rem; }
.gallery__arrow--next { right: 1.2rem; }
.gallery__stage:hover .gallery__arrow:not(:disabled),
.gallery__arrow:focus-visible { opacity: 1; }
.gallery__arrow:disabled { pointer-events: none; }

.gallery__enlarge {
  display: block;
  margin: 1.2rem auto 0;
  padding: 0.4rem 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 1.1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--stone);
  cursor: zoom-in;
}
.gallery__enlarge:hover { color: var(--ink); }
.gallery__enlarge-label--touch { display: none; }

.gallery__thumbs {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  /* Match the capped main image height so a long set of thumbs scrolls. */
  max-height: calc(100vh - 22rem);
  overflow-y: auto;
  scrollbar-width: none;
}
.gallery__thumbs::-webkit-scrollbar { display: none; }

.gallery__thumb {
  flex: 0 0 auto;
  width: 100%;
  aspect-ratio: 4 / 5;
  padding: 0;
  border: 1px solid transparent;
  background: none;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.2s ease, border-color 0.2s ease;
}
.gallery--landscape .gallery__thumb { aspect-ratio: 3 / 2; }
.gallery--square .gallery__thumb { aspect-ratio: 1; }

.gallery__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.gallery__thumb:hover { opacity: 0.85; }
.gallery__thumb.is-active {
  opacity: 1;
  border-color: var(--ink);
}

/* Lightbox (teleported to body, so scoped styles still apply via data attr) */
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: var(--canvas);
  display: flex;
  flex-direction: column;
}

.lightbox__close {
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  z-index: 2;
  width: 4.4rem;
  height: 4.4rem;
  border: 0;
  background: none;
  color: var(--ink);
  font-size: 3.4rem;
  line-height: 1;
  cursor: pointer;
}

.lightbox__scroller {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6.4rem 2.4rem 4rem;
  overflow: hidden;
}
.lightbox__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: zoom-in;
  user-select: none;
}

.lightbox__scroller.is-zoomed {
  display: block;
  padding: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.lightbox__scroller.is-zoomed .lightbox__img {
  max-width: none;
  max-height: none;
  width: max(100%, 1600px);
  cursor: zoom-out;
}

.lightbox__hint {
  position: absolute;
  left: 50%;
  bottom: 1.2rem;
  transform: translateX(-50%);
  font-size: 1.1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--stone);
  pointer-events: none;
}

@media (hover: none) {
  /* Touch devices swipe the track; arrows just get in the way. */
  .gallery__arrow { display: none; }
  .gallery__enlarge-label { display: none; }
  .gallery__enlarge-label--touch { display: inline; }
}

@media (max-width: 960px) {
  /* Not enough width for a side column on phones, so thumbs drop below. */
  .gallery { grid-template-columns: minmax(0, 1fr); gap: 1.2rem; }
  .gallery__main { grid-column: 1; }
  .gallery__thumbs {
    grid-row: 2;
    flex-direction: row;
    max-height: none;
    overflow-x: auto;
    overflow-y: visible;
  }
  .gallery__track { max-height: none; }
  .gallery__slide img { object-position: center top; }
  .gallery__thumb { flex: 0 0 6.4rem; }
  .gallery--landscape .gallery__thumb { flex-basis: 8.4rem; }
}
</style>
